import React, { useRef, useEffect, useState } from 'react';
import ThemeSelector from './ThemeSelector';

function SettingsSidebar({ 
  isOpen, 
  onClose, 
  currentTheme, 
  onThemeChange, 
  viewMode, 
  setViewMode,
  showArchived,
  toggleArchivedView,
  resetAllSettings,
  customThemeColors,
  exportSettings,
  importSettings,
  doodleSettings,
  updateDoodleSettings
}) {
  const sidebarRef = useRef(null);
  const [showThemeSettings] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const fileInputRef = useRef(null);
  const [isThemeSelectorOpen, setIsThemeSelectorOpen] = useState(false);
  const [showDoodleSettings, setShowDoodleSettings] = useState(false);
  const [tempDoodleSettings, setTempDoodleSettings] = useState(doodleSettings || {
    shapes: ['circle', 'triangle', 'star', 'square', 'hexagon', 'diamond', 'cross', 'zigzag', 'wave'],
    density: 20,
    colors: ['#ff5733', '#33ff57', '#3357ff'],
    minSize: 10,
    maxSize: 50,
    opacity: 0.4,
    strokeWidth: 2,
    animate: true,
    animationSpeed: 1,
    outlineOnly: true
  });

  // Custom theme color state
  const [tempCustomColors, setTempCustomColors] = useState(customThemeColors || {
    primary: '#6366f1',
    background: '#f9fafb',
    cardBg: '#ffffff',
    textPrimary: '#1f2937',
    textSecondary: '#6b7280',
    border: '#e5e7eb'
  });

  // Reset temp colors when sidebar opens
  useEffect(() => {
    if (isOpen && customThemeColors) {
      setTempCustomColors(customThemeColors);
    }
  }, [isOpen, customThemeColors]);


  

  // Close sidebar when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const openThemeSelector = () => {
    setIsThemeSelectorOpen(true);
  };

  const closeThemeSelector = () => {
    setIsThemeSelectorOpen(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      importSettings(event.target.result);
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const applyCustomTheme = () => {
    onThemeChange('custom-theme', tempCustomColors);
  };

  const resetCustomTheme = () => {
    setTempCustomColors({
        primary: '#6366f1',
        background: '#f9fafb',
        cardBg: '#ffffff',
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        border: '#e5e7eb'
    });
  };

  const applyDoodleSettings = () => {
    updateDoodleSettings(tempDoodleSettings);
    window.toast.success('Doodly changes applied');
  };
  
  // Reset to the original doodle settings
  const resetDoodleSettings = () => {
    setTempDoodleSettings(doodleSettings);
    window.toast.success('Doodly settings reframed');
  };

  return (
    <div className={`settings-sidebar ${isOpen ? 'open' : ''}`} ref={sidebarRef}>
      <div className="settings-header">
        <h2>Settings</h2>
        <button className="close-settings" onClick={onClose}>×</button>
      </div>
      
      <div className="settings-content">
        {/* Display Settings */}
        <div className="settings-section">
          <h3>Display Settings</h3>
          
          <div className="setting-group">
            <label>View Mode:</label>
            <div className="view-mode-buttons">
              <button 
                className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                Grid
              </button>
              <button 
                className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
              <button 
                className={`view-button ${viewMode === 'compact' ? 'active' : ''}`}
                onClick={() => setViewMode('compact')}
              >
                Compact
              </button>
            </div>
          </div>
          
          <div className="setting-group">
            <label>Theme:</label>
            <div style={{ marginTop: 'auto', padding: '1rem 0' }}>
                <button 
                    className="theme-change-button"
                    onClick={openThemeSelector}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
                    </svg>
                    Change Theme
                </button>
            </div>
          </div>
          
          {/* Custom Theme Settings */}
          {showThemeSettings && (
            <div className="custom-theme-settings">
              <h4>Custom Theme Colors</h4>
              
              <div className="color-picker-group">
                <label>Primary Color:</label>
                <input 
                  type="color" 
                  value={tempCustomColors.primary}
                  onChange={(e) => setTempCustomColors({...tempCustomColors, primary: e.target.value})}
                />
              </div>
              
              <div className="color-picker-group">
                <label>Background:</label>
                <input 
                  type="color" 
                  value={tempCustomColors.background}
                  onChange={(e) => setTempCustomColors({...tempCustomColors, background: e.target.value})}
                />
              </div>
              
              <div className="color-picker-group">
                <label>Card Background:</label>
                <input 
                  type="color" 
                  value={tempCustomColors.cardBg}
                  onChange={(e) => setTempCustomColors({...tempCustomColors, cardBg: e.target.value})}
                />
              </div>
              
              <div className="color-picker-group">
                <label>Text Primary:</label>
                <input 
                  type="color" 
                  value={tempCustomColors.textPrimary}
                  onChange={(e) => setTempCustomColors({...tempCustomColors, textPrimary: e.target.value})}
                />
              </div>
              
              <div className="color-picker-group">
                <label>Text Secondary:</label>
                <input 
                  type="color" 
                  value={tempCustomColors.textSecondary}
                  onChange={(e) => setTempCustomColors({...tempCustomColors, textSecondary: e.target.value})}
                />
              </div>
              
              <div className="color-picker-group">
                <label>Border Color:</label>
                <input 
                  type="color" 
                  value={tempCustomColors.border}
                  onChange={(e) => setTempCustomColors({...tempCustomColors, border: e.target.value})}
                />
              </div>
              
              <div className="theme-action-buttons">
                <button onClick={applyCustomTheme}>Apply Theme</button>
                <button onClick={resetCustomTheme}>Reset Colors</button>
              </div>
            </div>
          )}
        </div>
        
        {/* Content Settings */}
        <div className="settings-section">
        <h3>Content Settings</h3>
        
        <div className="setting-group">
            <div className="setting-toggle">
            <input 
                type="checkbox" 
                id="toggleArchived"
                checked={showArchived}
                onChange={toggleArchivedView}
            />
            <label htmlFor="toggleArchived">Show Archived Cards</label>
            </div>
        </div>
        </div>

        {/* Background Doodle Settings */}
        <div className="settings-section">
          <h3 onClick={() => setShowDoodleSettings(!showDoodleSettings)} style={{ cursor: 'pointer' }}>
            Background Doodles {showDoodleSettings ? '▼' : '►'}
          </h3>
                    
          {showDoodleSettings && (
            <>
              <div className="setting-group">
                <label>Density:</label>
                <div className="range-with-value">
                  <input 
                    type="range" 
                    min="0" 
                    max="50" 
                    value={tempDoodleSettings.density}
                    onChange={(e) => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      density: parseInt(e.target.value)
                    })}
                  />
                  <span>{tempDoodleSettings.density}</span>
                </div>
              </div>
              
              <div className="setting-group">
                <label>Minimum Size:</label>
                <div className="range-with-value">
                  <input 
                    type="range" 
                    min="5" 
                    max="30" 
                    value={tempDoodleSettings.minSize}
                    onChange={(e) => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      minSize: parseInt(e.target.value)
                    })}
                  />
                  <span>{tempDoodleSettings.minSize}px</span>
                </div>
              </div>
              
              <div className="setting-group">
                <label>Maximum Size:</label>
                <div className="range-with-value">
                  <input 
                    type="range" 
                    min="20" 
                    max="100" 
                    value={tempDoodleSettings.maxSize}
                    onChange={(e) => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      maxSize: parseInt(e.target.value)
                    })}
                  />
                  <span>{tempDoodleSettings.maxSize}px</span>
                </div>
              </div>
              
              <div className="setting-group">
                <label>Opacity:</label>
                <div className="range-with-value">
                  <input 
                    type="range" 
                    min="0" 
                    max="1" 
                    step="0.1" 
                    value={tempDoodleSettings.opacity}
                    onChange={(e) => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      opacity: parseFloat(e.target.value)
                    })}
                  />
                  <span>{tempDoodleSettings.opacity}</span>
                </div>
              </div>
              
              <div className="setting-group">
                <label>Stroke Width:</label>
                <div className="range-with-value">
                  <input 
                    type="range" 
                    min="1" 
                    max="5" 
                    value={tempDoodleSettings.strokeWidth}
                    onChange={(e) => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      strokeWidth: parseInt(e.target.value)
                    })}
                  />
                  <span>{tempDoodleSettings.strokeWidth}px</span>
                </div>
              </div>
              
              <div className="setting-group">
                <div className="setting-toggle">
                  <input 
                    type="checkbox" 
                    id="toggleAnimate"
                    checked={tempDoodleSettings.animate}
                    onChange={() => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      animate: !tempDoodleSettings.animate
                    })}
                  />
                  <label htmlFor="toggleAnimate">Animate Shapes</label>
                </div>
                {/* Add this section for conditional rendering of animation speed */}
                {tempDoodleSettings.animate && (
                  <div className="setting-group">
                    <label>Speed:</label>
                    <div className="range-with-value">
                      <input 
                        type="range" 
                        min="0.5" 
                        max="3" 
                        step="0.5" 
                        value={tempDoodleSettings.animationSpeed || 1.5}
                        onChange={(e) => setTempDoodleSettings({
                          ...tempDoodleSettings, 
                          animationSpeed: parseFloat(e.target.value)
                        })}
                      />
                      <span>{tempDoodleSettings.animationSpeed || 1.5}</span>
                    </div>
                  </div>
                )}
              </div>

              
              
              <div className="setting-group">
                <div className="setting-toggle">
                  <input 
                    type="checkbox" 
                    id="toggleOutlineOnly"
                    checked={tempDoodleSettings.outlineOnly}
                    onChange={() => setTempDoodleSettings({
                      ...tempDoodleSettings, 
                      outlineOnly: !tempDoodleSettings.outlineOnly
                    })}
                  />
                  <label htmlFor="toggleOutlineOnly">Outline Only</label>
                </div>
              </div>

              
              
              <div className="setting-group">
                <label>Shape Types:</label>
                <div className="shape-checkboxes">
                  {['circle', 'triangle', 'star', 'square', 'hexagon', 'diamond', 'cross', 'zigzag', 'wave'].map(shape => (
                    <div key={shape} className="shape-option">
                      <input 
                        type="checkbox" 
                        id={`shape-${shape}`}
                        checked={tempDoodleSettings.shapes.includes(shape)}
                        onChange={() => {
                          const shapes = tempDoodleSettings.shapes.includes(shape)
                            ? tempDoodleSettings.shapes.filter(s => s !== shape)
                            : [...tempDoodleSettings.shapes, shape];
                          setTempDoodleSettings({...tempDoodleSettings, shapes});
                        }}
                      />
                      <label htmlFor={`shape-${shape}`}>{shape}</label>
                    </div>
                  ))}
                </div>
              </div>

              
              
              <div className="setting-group">
                <label>Colors:</label>
                <div className="color-list">
                  {tempDoodleSettings.colors.map((color, index) => (
                    <div key={index} className="color-item">
                      <input 
                        type="color" 
                        value={color}
                        onChange={(e) => {
                          const newColors = [...tempDoodleSettings.colors];
                          newColors[index] = e.target.value;
                          setTempDoodleSettings({...tempDoodleSettings, colors: newColors});
                        }}
                      />
                      <button 
                        className="color-remove-btn"
                        onClick={() => {
                          const newColors = tempDoodleSettings.colors.filter((_, i) => i !== index);
                          setTempDoodleSettings({...tempDoodleSettings, colors: newColors});
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button 
                    className="color-add-btn"
                    onClick={() => {
                      const newColors = [...tempDoodleSettings.colors, '#000000'];
                      setTempDoodleSettings({...tempDoodleSettings, colors: newColors});
                    }}
                  >
                    Add Color
                  </button>
                </div>
              </div>
              
              <div className="theme-action-buttons">
                <button onClick={applyDoodleSettings}>Apply Changes</button>
                <button onClick={resetDoodleSettings}>Reset Changes</button>
              </div>
            </>
          )}
        </div>
        
        {/* Advanced Settings */}
        <div className="settings-section">
          <h3 onClick={() => setShowAdvancedSettings(!showAdvancedSettings)} style={{ cursor: 'pointer' }}>
            Advanced Settings {showAdvancedSettings ? '▼' : '►'}
          </h3>
          
          {showAdvancedSettings && (
            <>
              <div className="setting-group">
                <button onClick={exportSettings} className="full-width-button">
                  Export All Settings
                </button>
              </div>
              
              <div className="setting-group">
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  accept=".json" 
                  onChange={handleFileChange} 
                />
                <button onClick={handleImportClick} className="full-width-button">
                  Import Settings
                </button>
              </div>
              
              <div className="setting-group">
                <button onClick={resetAllSettings} className="full-width-button danger-button">
                  Reset All Settings
                </button>
              </div>
            </>
          )}
          {isThemeSelectorOpen && (
            <ThemeSelector 
            isOpen={isThemeSelectorOpen}
            onClose={closeThemeSelector}
            onThemeChange={onThemeChange}
            currentTheme={currentTheme}
            />
        )}
        </div>
      </div>
    </div>
  );
}

export default SettingsSidebar;